import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { CreditWalletKind } from '@this+that/models';

export interface CreditBalance {
  hasWallet: boolean;
  planAllowanceMicros: number;
  planRemainingMicros: number;
  planRenewsAt?: number | null;
  purchasedMicros: number;
  totalSpendableMicros: number;
}

export interface TeamCreditBalance {
  teamId: string;
  purchasedMicros: number;
  totalSpendableMicros: number;
  memberCount: number;
  canPurchase: boolean;
}

export interface CreditPackCurrencyOption {
  currency: string;
  unitAmount: number;
}

export interface CreditPack {
  lookupKey: string;
  credits: number;
  unitAmount: number;
  currency: string;
  currencyOptions?: CreditPackCurrencyOption[] | null;
}

const GET_MY_CREDITS = gql`
  query myCredits {
    myCredits {
      hasWallet
      planAllowanceMicros
      planRemainingMicros
      planRenewsAt
      purchasedMicros
      totalSpendableMicros
    }
  }
`;

interface GetMyCreditsData {
  myCredits: CreditBalance;
}

export interface UseGetMyCreditsOptions {
  skip?: boolean;
  pollInterval?: number;
}

export function useGetMyCredits(options?: UseGetMyCreditsOptions) {
  return useQuery<GetMyCreditsData>(GET_MY_CREDITS, {
    skip: options?.skip,
    fetchPolicy: 'cache-and-network',
    pollInterval: options?.pollInterval,
  });
}

const GET_TEAM_CREDITS = gql`
  query teamCredits($teamId: ID!) {
    teamCredits(teamId: $teamId) {
      teamId
      purchasedMicros
      totalSpendableMicros
      memberCount
      canPurchase
    }
  }
`;

interface GetTeamCreditsData {
  teamCredits: TeamCreditBalance | null;
}

export interface UseGetTeamCreditsOptions {
  skip?: boolean;
  pollInterval?: number;
}

export function useGetTeamCredits(
  teamId: string | undefined,
  options?: UseGetTeamCreditsOptions,
) {
  return useQuery<GetTeamCreditsData>(GET_TEAM_CREDITS, {
    variables: { teamId: teamId ?? '' },
    skip: options?.skip || !teamId,
    fetchPolicy: 'cache-and-network',
    pollInterval: options?.pollInterval,
  });
}

const GET_CREDIT_PACKS = gql`
  query creditPacks {
    creditPacks {
      lookupKey
      credits
      unitAmount
      currency
      currencyOptions
    }
  }
`;

interface GetCreditPacksData {
  creditPacks: CreditPack[];
}

export function useGetCreditPacks(skip = false) {
  return useQuery<GetCreditPacksData>(GET_CREDIT_PACKS, { skip });
}

const PURCHASE_CREDITS = gql`
  mutation purchaseCredits(
    $walletKind: CreditWalletKind!
    $teamId: String
    $lookupKey: String!
    $currency: String
  ) {
    purchaseCredits(
      walletKind: $walletKind
      teamId: $teamId
      lookupKey: $lookupKey
      currency: $currency
    )
  }
`;

export interface PurchaseCreditsInput {
  walletKind: CreditWalletKind;
  teamId?: string;
  lookupKey: string;
  currency?: string;
}

interface PurchaseCreditsData {
  purchaseCredits: string;
}

export function usePurchaseCredits() {
  const [purchaseCreditsMutation, { loading, error }] = useMutation<
    PurchaseCreditsData,
    PurchaseCreditsInput
  >(PURCHASE_CREDITS);

  const purchaseCredits = useCallback(
    async (input: PurchaseCreditsInput) => {
      const { data } = await purchaseCreditsMutation({ variables: input });
      return data?.purchaseCredits;
    },
    [purchaseCreditsMutation],
  );

  return { purchaseCredits, loading, error };
}

// The backend has no shared GraphQL types for these yet — declared locally,
// same reasoning as ExecutionsPerMonthBasis/FlowCost* in flowCostEstimate.service.ts.
export enum CreditScope {
  PERSONAL = 'PERSONAL',
  TEAM = 'TEAM',
}

export enum CreditTransactionKind {
  FLOW = 'FLOW',
  ASSISTANT = 'ASSISTANT',
  AGENT = 'AGENT',
  PURCHASE = 'PURCHASE',
  PLAN = 'PLAN',
  CREDIT = 'CREDIT',
  OTHER = 'OTHER',
}

export enum CreditTransactionFilter {
  ALL = 'ALL',
  FLOWS = 'FLOWS',
  ASSISTANT = 'ASSISTANT',
  AGENTS = 'AGENTS',
  PURCHASES = 'PURCHASES',
}

export interface CreditBreakdownItem {
  key: string;
  creditsMicros: number;
}

export interface CreditTransaction {
  id: string;
  kind: CreditTransactionKind;
  name: string | null;
  byName: string | null;
  createdAt: number;
  amountMicros: number;
  balanceAfterMicros: number | null;
  modelCalls: number | null;
  breakdown: CreditBreakdownItem[];
  reference: string | null;
}

export interface CreditTransactionPage {
  items: CreditTransaction[];
  nextCursor: string | null;
}

const GET_CREDIT_TRANSACTIONS = gql`
  query creditTransactions(
    $scope: CreditScope!
    $teamId: ID
    $filter: CreditTransactionFilter
    $cursor: String
    $limit: Int
  ) {
    creditTransactions(
      scope: $scope
      teamId: $teamId
      filter: $filter
      cursor: $cursor
      limit: $limit
    ) {
      items {
        id
        kind
        name
        byName
        createdAt
        amountMicros
        balanceAfterMicros
        modelCalls
        breakdown {
          key
          creditsMicros
        }
        reference
      }
      nextCursor
    }
  }
`;

interface GetCreditTransactionsData {
  creditTransactions: CreditTransactionPage;
}

export interface GetCreditTransactionsVariables {
  scope: CreditScope;
  teamId?: string;
  filter?: CreditTransactionFilter;
  cursor?: string;
  limit?: number;
}

export interface UseGetCreditTransactionsOptions {
  skip?: boolean;
  pollInterval?: number;
}

export function useGetCreditTransactions(
  variables: GetCreditTransactionsVariables,
  options?: UseGetCreditTransactionsOptions,
) {
  return useQuery<GetCreditTransactionsData, GetCreditTransactionsVariables>(
    GET_CREDIT_TRANSACTIONS,
    {
      variables,
      skip: options?.skip,
      fetchPolicy: 'cache-and-network',
      pollInterval: options?.pollInterval,
      notifyOnNetworkStatusChange: true,
    },
  );
}
